import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { inject } from '@angular/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { Locale, locales } from '../locales';
import { StorageKeyConstants } from '../constants/storage-key.constants';
import { take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DateAdapter } from '@angular/material/core';

type LanguageState = {
  localesList: Locale[];
  locale: Locale;
};

const initialState: LanguageState = {
  localesList: locales.localesList,
  locale: locales.defaultLanguge,
};

export const LanguageStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods((store, translateService = inject(TranslateService)) => {
    const dateAdapter = inject(DateAdapter, { optional: true });
    return {
      changeLanguage(locale: Locale) {
        const dateAdapterLocale = locale.code === 'en' ? 'en-GB' : locale.code;

        translateService
          .use(locale.code)
          .pipe(take(1))
          .subscribe(() => {
            localStorage.setItem(StorageKeyConstants.LANGUAGE_KEY, JSON.stringify(locale));

            dateAdapter?.setLocale(dateAdapterLocale);
          });
      },
      _initializeLanguageChange() {
        let currentLocale = store.locale();

        try {
          const storedLocale = localStorage.getItem(StorageKeyConstants.LANGUAGE_KEY);
          const parsedLocale = storedLocale ? JSON.parse(storedLocale) : null;

          if (isLocale(parsedLocale)) {
            currentLocale = parsedLocale;
          }
        } catch {
          // malformed JSON, fallback to default
        }

        patchState(store, { locale: currentLocale });

        this.changeLanguage(currentLocale);

        translateService.onLangChange.pipe(takeUntilDestroyed()).subscribe((event: LangChangeEvent) => {
          const locale = store.localesList().find((x) => x.code === event.lang);
          if (locale) {
            patchState(store, { locale });
            dateAdapter?.setLocale(locale.code);
          }
        });
      },
    };
  }),
  withHooks({
    onInit(store) {
      store._initializeLanguageChange();
    },
  }),
);

function isLocale(value: any): value is Locale {
  return (
    typeof value === 'object' &&
    value !== null &&
    'code' in value &&
    typeof value.code === 'string' &&
    'label' in value &&
    typeof value.label === 'string'
  );
}
